import { Injectable, Injector, OnDestroy, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import {
  VENDOR_ORDER_RING_MELODY,
  melodyToWavDataUrl,
} from './vendor-notification-sound.util';
import { VendorOrderRing } from './vendor-order-ring.plugin';
import { VendorOrdersService } from '../core/services/vendor-orders.service';

export type StartContinuousRingOptions = {
  /**
   * Allow the first clip even when tabCounts.new is still 0 (fresh new-order event
   * before the server count refresh lands). Further loops still require new > 0.
   */
  seedPending?: boolean;
  apiUrl?: string;
  token?: string;
};

/**
 * Plays continuous order ring audio until stopped (new order / reminder).
 * Native Android: MediaPlayer clip-by-clip via VendorOrderRing (next loop only if New > 0).
 * Web: HTMLAudio clip-by-clip with the same gate.
 */
@Injectable({ providedIn: 'root' })
export class VendorNativeAlarmService implements OnDestroy {
  private readonly injector = inject(Injector);
  private readonly clipCache = new Map<string, string>();
  private readonly destroy$ = new Subject<void>();
  private ringAudio: HTMLAudioElement | null = null;
  private ringing = false;
  private webVolume = 1;
  private countWatchSub: Subscription | null = null;
  private lastRingAllowed: boolean | null = null;

  get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  /** True once JS has started the continuous ring (may lag native FCM-started ring). */
  get isRinging(): boolean {
    return this.ringing;
  }

  /** Includes native MediaPlayer / FGS started from FCM without going through JS. */
  async isRingPlaying(): Promise<boolean> {
    if (this.ringing) {
      return true;
    }
    if (!this.isNative) {
      return false;
    }
    try {
      const result = await VendorOrderRing.isRinging();
      return !!result?.ringing;
    } catch {
      return false;
    }
  }

  /** Persist session so a background native ring can poll existing tab-counts. */
  syncWatch(apiUrl?: string | null, token?: string | null): void {
    if (!this.isNative || !apiUrl || !token) {
      return;
    }
    void VendorOrderRing.configureWatch({ apiUrl, token }).catch(() => {});
  }

  clearWatch(): void {
    if (!this.isNative) {
      return;
    }
    void VendorOrderRing.clearWatch().catch(() => {});
  }

  /**
   * Start continuous loud alert ring until {@link stopAllSounds}.
   * Each subsequent clip/loop runs only while unanswered New-tab orders remain.
   */
  startContinuousRing(volume = 1, options?: StartContinuousRingOptions): void {
    const newCount = this.peekNewCount();
    if (newCount <= 0 && !options?.seedPending) {
      void this.syncRingAllowed(false);
      return;
    }

    if (this.ringing) {
      void this.syncRingAllowed(newCount > 0 || !!options?.seedPending);
      return;
    }

    this.ringing = true;
    this.webVolume = volume;
    void this.syncRingAllowed(true);
    this.ensureCountWatcher();

    if (this.isNative) {
      void VendorOrderRing.startRing({
        sound: 'vendor_order_ring',
        apiUrl: options?.apiUrl,
        token: options?.token,
      }).catch(() => {});
      return;
    }
    this.playWebClip();
  }

  playNewOrderFanfare(volume = 1, watch?: { apiUrl?: string; token?: string }): void {
    this.startContinuousRing(volume, { seedPending: true, ...watch });
  }

  playPendingReminder(volume = 1, watch?: { apiUrl?: string; token?: string }): void {
    this.startContinuousRing(volume, watch);
  }

  stopReminder(): void {
    this.stopAllSounds();
  }

  /** Sync whether the next native/web clip may play (New-tab unanswered count). */
  async setContinueAllowed(allowed: boolean): Promise<void> {
    await this.syncRingAllowed(allowed);
    if (!allowed && this.ringing) {
      this.stopAllSounds();
    }
  }

  stopAllSounds(): void {
    this.ringing = false;
    this.teardownCountWatcher();
    void this.syncRingAllowed(false);
    if (this.isNative) {
      void VendorOrderRing.stopRing().catch(() => {});
    }
    if (this.ringAudio) {
      this.ringAudio.onended = null;
      this.ringAudio.pause();
      this.ringAudio.currentTime = 0;
      this.ringAudio = null;
    }
  }

  ngOnDestroy(): void {
    this.stopAllSounds();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private peekNewCount(): number {
    try {
      return this.injector.get(VendorOrdersService).peekNewOrderCount();
    } catch {
      return 0;
    }
  }

  private ensureCountWatcher(): void {
    if (this.countWatchSub) {
      return;
    }
    try {
      const orders = this.injector.get(VendorOrdersService);
      this.countWatchSub = orders.vendorTabCounts$
        .pipe(
          map((c) => c.new),
          distinctUntilChanged(),
          takeUntil(this.destroy$),
        )
        .subscribe((newCount) => {
          if (!this.ringing) {
            return;
          }
          if (newCount <= 0) {
            this.stopAllSounds();
            return;
          }
          void this.syncRingAllowed(true);
        });
    } catch {
      // Orders service unavailable (tests) — ring without count gate.
    }
  }

  private teardownCountWatcher(): void {
    this.countWatchSub?.unsubscribe();
    this.countWatchSub = null;
  }

  private async syncRingAllowed(allowed: boolean): Promise<void> {
    if (this.lastRingAllowed === allowed) {
      return;
    }
    this.lastRingAllowed = allowed;
    if (!this.isNative) {
      return;
    }
    try {
      await VendorOrderRing.setRingAllowed({ allowed });
    } catch {
      // ignore
    }
  }

  private playWebClip(): void {
    if (typeof window === 'undefined' || !this.ringing) {
      return;
    }
    if (this.peekNewCount() <= 0 && this.lastRingAllowed === false) {
      this.stopAllSounds();
      return;
    }
    try {
      const cacheKey = 'order-ring-alert-v2';
      let url = this.clipCache.get(cacheKey);
      if (!url) {
        url = melodyToWavDataUrl(VENDOR_ORDER_RING_MELODY);
        this.clipCache.set(cacheKey, url);
      }
      if (this.ringAudio) {
        this.ringAudio.onended = null;
        this.ringAudio.pause();
      }
      const audio = new Audio(url);
      audio.loop = false;
      audio.volume = Math.min(1, this.webVolume);
      audio.onended = () => {
        if (!this.ringing) {
          return;
        }
        if (this.peekNewCount() <= 0) {
          this.stopAllSounds();
          return;
        }
        this.playWebClip();
      };
      this.ringAudio = audio;
      void audio.play().catch(() => {});
    } catch {
      // ignore
    }
  }
}
